import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { NgxSpinnerService } from 'ngx-spinner';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { CommonSearchComponent } from '../common-component-search/common-component-search.component';
import { CommonApiService } from '../CommonController/common-api-service';
import { governmentServerApiServer } from '../CommonController/governmentServer-api-service';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { MatOption } from '@angular/material/core';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import {
  MatFormField,
  MatLabel,
  MatError,
  MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { CommonDropdownSearchComponent } from '../common-component-dropdown-search/common-component-dropdown-search.component';
import { SharedValidators } from '../shared/validators/shared-validators';
import { FirmwareMasterApiServer } from '../CommonController/firmware-master-service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-firmware-master',
  standalone: true,
  imports: [
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    CommonSearchComponent,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSelect,
    MatOption,
    DatePipe,
    CommonPaginationComponent,
  ],
  templateUrl: './firmware-master.component.html',
  styleUrl: './firmware-master.component.scss',
})
export class FirmwareMasterComponent implements OnInit {
  serverName = environment.projectName;

  // Pagination settings
  pageSize = 10; // Number of items per currentPage
  currentPage = 1; // Current currentPage
  searchText = ''; // Search text

  // Data arrays
  displayedItems = []; // List of items displayed on the current currentPage
  totalItems = 0; // Total number of items (for pagination purposes)
  totalPages = 0; // Total number of items (for pagination purposes)

  // Form group for handling the form
  firmwareFilterForm: FormGroup;
  addUpdateFirmwareForm: FormGroup; // Form for adding or updating firmware details

  // User-related data
  loggedInUserId = null; // Logged in user ID (could be null or string)
  userRole = ''; // Role of the logged-in user (e.g., 'support', 'manager')
  userPermissions = []; // User permissions object

  // Permissions related to gov servers
  canViewFirmwareMaster = false;
  canCreateFirmwareMaster = false;
  canUpdateFirmwareMaster = false;
  canDeleteFirmwareMaster = false;
  firmwareFormType = 'Add'; // Type of form: 'Add' or 'View'
  today: Date = new Date();
  viewType = 'List'; // View type: 'List' or 'Details'

  firmwareTypes = [
    { value: 'OC', name: 'Open CPU' },
    { value: 'D', name: 'Device' },
  ];

  constructor(
    public firmwareMasterApiServer: FirmwareMasterApiServer,
    public commonApiService: CommonApiService,
    private formBuilder: FormBuilder,
    public router: Router,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.viewType = 'List'; // Initialize view type to 'List'
    this.checkPermission();
    this.setFirmwareFilterForm();
    this.fetchAllFirmwareList();
    this.setAddUpdateFirmwareForm();
  }

  checkPermission() {
    this.userPermissions = JSON.parse(
      sessionStorage.getItem('userPermissions')
    );
    this.loggedInUserId = sessionStorage.getItem('loggedInUserId');
    this.userPermissions.forEach((element) => {
      if (element.pageName === 'Firmware Master' && element.view === false) {
        this.navigateBack();
      }
      if (element.pageName === 'Firmware Master' && element.view === true) {
        this.canViewFirmwareMaster = true;
      }
      if (element.pageName === 'Firmware Master' && element.create === true) {
        this.canCreateFirmwareMaster = true;
      }
      if (element.pageName === 'Firmware Master' && element.update === true) {
        this.canUpdateFirmwareMaster = true;
      }
      if (element.pageName === 'Firmware Master' && element.delete === true) {
        this.canDeleteFirmwareMaster = true;
      }
    });
  }

  setFirmwareFilterForm() {
    this.firmwareFilterForm = this.formBuilder.group({
      myActivationsSearch: new FormControl(''),
    });
  }

  setAddUpdateFirmwareForm() {
    this.addUpdateFirmwareForm = this.formBuilder.group({
      id: new FormControl(null),
      firmwareName: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.rangeLength(1, 50),
      ]),
      description: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.rangeLength(1, 50),
      ]),
      releaseDate: new FormControl('', [Validators.required]),
      userId: new FormControl(this.loggedInUserId, [Validators.required]),
      fileName: new FormControl('', [Validators.required]),
      firmwareType: new FormControl('D', [Validators.required]),
    });
  }

  fetchAllFirmwareList() {
    let firmwareType = this.serverName === 'LCT-A4G' ? '' : 'D';
    this.spinner.show();
    this.firmwareMasterApiServer
      .getAllFirmwareList(
        this.currentPage - 1,
        this.pageSize,
        this.searchText,
        firmwareType
      )
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          if (res.status) {
            this.displayedItems = res.data;
            this.totalItems = res.totalItems;
            this.totalPages = res.totalPages;
            this.commonApiService.openSnackbar(res.message);
          } else {
            this.displayedItems = [];
            this.totalItems = 0;
            this.totalPages = 0;
            this.commonApiService.openSnackbar(res.message);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.displayedItems = [];
          this.totalItems = 0;
          this.totalPages = 0;
          this.spinner.hide();
          this.commonApiService.openSnackbar(error.error.message);
        },
      });
  }

  onSearchInputChange(event) {
    this.currentPage = 1;
    this.searchText = event;
    this.fetchAllFirmwareList();
  }

  deleteFirmware(id): void {
    // Confirmation prompt
    if (confirm('Are you sure you want to delete this firmware?')) {
      this.spinner.show();
      this.firmwareMasterApiServer
        .deleteFirmware(id, this.loggedInUserId)
        .subscribe({
          next: (res) => {
            this.spinner.hide();
            if (res.status) {
              this.commonApiService.openSnackbar(res.message);
              this.ngOnInit();
            } else {
              this.commonApiService.openSnackbar(res.message);
            }
          },
          error: (ERROR: HttpErrorResponse) => {
            this.commonApiService.openSnackbar(ERROR.error.message);
            this.spinner.hide();
          },
        });
    } else {
      this.commonApiService.openSnackbar('Delete cancelled by the user.');
    }
  }

  onPageChange(event) {
    this.currentPage = event;
    this.fetchAllFirmwareList();
  }

  onSizeChange(pageSize): void {
    this.pageSize = pageSize; // Update the pageSize
    this.currentPage = 1; // Reset to the first currentPage
    this.fetchAllFirmwareList();
  }

  AddNew() {
    this.router.navigate(['govt-servers-add']);
  }

  viewGSDetails(id): void {
    this.router.navigate(['govt-servers-add', id]);
  }

  redirect(to): void {
    this.router.navigate([to]);
  }

  addFirmware(type: string) {
    this.viewType = 'Details';
    this.firmwareFormType = type;
    this.addUpdateFirmwareForm.reset();
    this.addUpdateFirmwareForm.get('userId').setValue(this.loggedInUserId);
    this.addUpdateFirmwareForm.get('releaseDate').setValue(new Date());
    this.addUpdateFirmwareForm.get('firmwareType').setValue('D');
    this.selectedFile = null; // Reset the selected file
  }

  get firmwarePageTitle(): string {
    switch (this.firmwareFormType) {
      case 'Add':
        return 'Add Firmware';
      case 'View':
        return 'View Firmware';
      default:
        return 'Firmware';
    }
  }

  navigateBack() {
    this.commonApiService.back();
  }

  onBack() {
    this.viewType = 'List';
  }

  onRefresh() {
    this.addUpdateFirmwareForm.reset();
    this.addUpdateFirmwareForm.get('userId').setValue(this.loggedInUserId);
    this.addUpdateFirmwareForm.get('releaseDate').setValue(new Date());
  }

  submitFirmwareDetails() {
    if (this.addUpdateFirmwareForm.invalid) {
      SharedValidators.markAllAsTouched(this.addUpdateFirmwareForm);
      SharedValidators.findInvalidControls(
        this.addUpdateFirmwareForm,
        this.addUpdateFirmwareForm
      );
    } else {
      const payload = this.addUpdateFirmwareForm.value;
      payload.userId = this.loggedInUserId;
      this.spinner.show();
      const firmwareDto = JSON.stringify({
        id: payload.id,
        firmwareName: payload.firmwareName,
        description: payload.description,
        releaseDate: payload.releaseDate,
        userId: payload.userId,
        firmwareType: payload.firmwareType,
      });
      const formData = new FormData();
      formData.append('firmwareDto', firmwareDto);

      // Ensure the file is attached
      if (this.selectedFile) {
        formData.append('binFile', this.selectedFile, this.selectedFile.name);
      } else {
        this.commonApiService.openSnackbar(
          'Please select a valid .pak or .pac or .bin or .tar file.'
        );
        return;
      }
      this.firmwareMasterApiServer.addFirmware(formData).subscribe({
        next: (res) => {
          this.spinner.hide();
          if (res.status) {
            this.commonApiService.openSnackbar(res.message);
            this.ngOnInit();
          } else {
            this.commonApiService.openSnackbar(res.message);
          }
        },
        error: (ERROR: HttpErrorResponse) => {
          this.commonApiService.openSnackbar(ERROR.error.message);
          this.spinner.hide();
        },
      });
    }
  }

  viewFMDetails(id: number) {
    this.viewType = 'Details';
    this.firmwareFormType = 'View';
    this.addUpdateFirmwareForm.reset();
    this.firmwareMasterApiServer.getFirmwareDetailsById(id).subscribe({
      next: (res) => {
        if (res.status) {
          const firmware = res.data;
          this.addUpdateFirmwareForm.patchValue({
            id: firmware.id,
            firmwareName: firmware.firmwareName,
            description: firmware.description,
            releaseDate: new Date(firmware.releaseDate),
            userId: this.loggedInUserId,
            fileName: firmware.fileName,
            firmwareType: firmware.firmwareType == 'OC' ? 'Open CPU' : 'Device',
          });
          this.selectedFile = null; // Reset the selected file
        } else {
          this.commonApiService.openSnackbar('No firmware data available.');
        }
      },
      error: (error: HttpErrorResponse) => {
        this.commonApiService.openSnackbar(error.error.message);
      },
    });
  }

  selectedFile: File | null = null;
  file_upload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const isPakOrBin =
        file.name.endsWith('.pak') ||
        file.name.endsWith('.bin') ||
        file.name.endsWith('.pac') ||
        file.name.endsWith('.tar');
      if (isPakOrBin) {
        this.selectedFile = file;
        this.addUpdateFirmwareForm.get('fileName').setValue(file.name);
      } else {
        this.addUpdateFirmwareForm
          .get('fileName')
          ?.setErrors({ invalidFileType: true });
        this.commonApiService.openSnackbar(
          'Please select only a .pak or .bin or .pac or .tar file.'
        );
      }
    }
  }
}
